import { ClickAwayListener } from "@mui/base";
import { Box, Menu, MenuItem, Tooltip } from "@mui/joy";
import { useMemo } from "react";

import { useUpdateTaskMutation } from "@/hooks/tasks";
import { useCurrentVenueUuid } from "@/hooks/venues";

import {
  TaskContextMenu as ContextMenuType,
  useTaskSetOverviewStore,
} from "../TaskSetOverview/useTaskSetOverview";
import { useTaskDeleteAction } from "./useTaskDeleteAction";

const useVirtualElement = (contextMenu: ContextMenuType | null) => {
  return useMemo(() => {
    if (!contextMenu) {
      return undefined;
    }
    const getBoundingClientRect = () => ({
      top: contextMenu.y,
      left: contextMenu.x,
      right: contextMenu.x,
      bottom: contextMenu.y,
      width: 0,
      height: 0,
      x: contextMenu.x,
      y: contextMenu.y,
      toJSON: () => "",
    });
    return {
      getBoundingClientRect,
    };
  }, [contextMenu]);
};

const TaskContextMenu = () => {
  const { contextMenu, setContextMenu } = useTaskSetOverviewStore();
  const venueUuid = useCurrentVenueUuid();
  const virtualElement = useVirtualElement(contextMenu);

  const updateMutation = useUpdateTaskMutation(venueUuid);
  const deleteAction = useTaskDeleteAction(contextMenu?.task);
  if (contextMenu === null) return null;

  return (
    <ClickAwayListener onClickAway={() => setContextMenu(null)}>
      <Menu size="sm" anchorEl={virtualElement} open placement="bottom-start">
        <MenuItem
          onClick={() => {
            updateMutation.mutate({
              uuid: contextMenu.task.uuid,
              flagged: !contextMenu.task.flagged,
            });
            setContextMenu(null);
          }}
        >
          {contextMenu.task.flagged ? "Unflag" : "Flag"}
        </MenuItem>
        <Tooltip title={deleteAction.tooltip ?? ""} disableHoverListener={!deleteAction.tooltip}>
          {/* A disabled menu item emits no pointer events, so the tooltip needs
              a wrapper to hang off. The item renders as a div so the wrapper can
              be the list item the menu expects. */}
          <Box component="li" sx={{ listStyle: "none" }}>
            <MenuItem
              component="div"
              disabled={deleteAction.disabled}
              onClick={() => {
                deleteAction.onClick();
                setContextMenu(null);
              }}
            >
              {deleteAction.label}
            </MenuItem>
          </Box>
        </Tooltip>
      </Menu>
    </ClickAwayListener>
  );
};

export default TaskContextMenu;
